'use client';

import Link from 'next/link';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import ArrowForwardIcon from '@mui/icons-material/ArrowForwardOutlined';
import Navigation from '../../../components/Navigation';
import {
    DocAnswer,
    DocBeforeAfter,
    DocCode,
    DocEyebrow,
    DocMistakeList,
    DocRelated,
    DocSection,
    DocSubsection,
    DocSubtitle,
    DocText,
    DocTitle,
} from '../../../components/DocArticle';
import { encodeBase64 } from '../../../utils/base64';

const generateExampleUrl = (() => {
    const oldEncoded = encodeBase64(JSON.stringify({ items: ['a', 'b'] }, null, 2));
    const newEncoded = encodeBase64(JSON.stringify({ items: ['a', 'b', 'c'] }, null, 2));
    return `/?old=${oldEncoded}&new=${newEncoded}`;
})();

const applyExampleUrl = (() => {
    const oldEncoded = encodeBase64(JSON.stringify({ items: ['a', 'b', 'c'] }, null, 2));
    const patchEncoded = encodeBase64(JSON.stringify([{ op: 'remove', path: '/items/1' }], null, 2));
    return `/?old=${oldEncoded}&patch=${patchEncoded}&mode=apply`;
})();

const mistakes = [
    {
        mistake: 'Removing several elements from low to high index',
        fix: 'Each remove shifts everything after it one position left. To remove "b" and "d" from ["a","b","c","d"], remove /items/3 first, then /items/1 - or recount after every remove.',
    },
    {
        mistake: 'Expecting add to overwrite an existing element',
        fix: 'add never overwrites an array element: it inserts before the index you give. To change an element in place, use replace.',
    },
    {
        mistake: 'Using /- with remove, replace or test',
        fix: '"-" names a position one past the last element. It only makes sense with add. remove /items/- fails because there is no element there to remove.',
    },
    {
        mistake: 'Writing array indexes with leading zeros',
        fix: '/items/01 is invalid. RFC 6902 array indexes are base-10 without leading zeros: /items/1.',
    },
    {
        mistake: 'Trying to address all elements at once',
        fix: 'There is no wildcard syntax. /items/* looks for an object key literally named "*". Emit one operation per element instead - the generator does this for you.',
    },
];

export default function JsonPatchArrayPage() {
    return (
        <>
            <Navigation activePage="docs" />
            <main>
                <Container maxWidth="lg" sx={{ py: { xs: 4, sm: 6 }, px: { xs: 2, sm: 3 } }}>
                    {/* Breadcrumb */}
                    <Box component="nav" sx={{ display: 'flex', mb: 4, fontSize: '0.875rem', color: 'var(--fg-muted)' }}>
                        <Link href="/" style={{ textDecoration: 'none', color: 'var(--fg-muted)' }}>
                            <Typography component="span" sx={{ '&:hover': { color: 'var(--accent)' }, fontSize: '0.875rem' }}>Home</Typography>
                        </Link>
                        <Typography component="span" sx={{ mx: 1, fontSize: '0.875rem', color: 'var(--fg-muted)' }}>/</Typography>
                        <Link href="/docs" style={{ textDecoration: 'none', color: 'var(--fg-muted)' }}>
                            <Typography component="span" sx={{ '&:hover': { color: 'var(--accent)' }, fontSize: '0.875rem' }}>Documentation</Typography>
                        </Link>
                        <Typography component="span" sx={{ mx: 1, fontSize: '0.875rem', color: 'var(--fg-muted)' }}>/</Typography>
                        <Typography component="span" sx={{ color: 'var(--fg)', fontSize: '0.875rem' }}>JSON Patch and Arrays</Typography>
                    </Box>

                    <Box sx={{ maxWidth: '800px' }}>
                        <DocEyebrow>Documentation</DocEyebrow>
                        <DocTitle>JSON Patch and Arrays</DocTitle>
                        <DocSubtitle>
                            Arrays are where JSON Patch (RFC 6902) trips most developers up. Every array rule -
                            appending with <code>/-</code>, inserting at an index, removing without off-by-one bugs -
                            with examples you can run in the tool.
                        </DocSubtitle>

                        <DocAnswer>
                            To append a value to a JSON array with JSON Patch, use the <strong>add</strong> operation
                            with a path ending in <code>/-</code>: <code>{'{ "op": "add", "path": "/items/-", "value": "new item" }'}</code>.
                            To insert at a position, use the numeric index. To delete, use <strong>remove</strong> with
                            the index - and remember every later element shifts one position left.
                        </DocAnswer>

                        <DocSection>The rules in one minute</DocSection>
                        <DocText>
                            Arrays are addressed by zero-based numeric indexes inside a{' '}
                            <Link href="/docs/json-pointer" style={{ color: 'var(--accent)' }}>JSON Pointer</Link> path.
                            Four rules cover almost everything:
                        </DocText>
                        <Box component="ul" sx={{ color: 'var(--fg-muted)', lineHeight: 1.8, pl: 3, mb: 3 }}>
                            <li><code>add</code> at an index <strong>inserts before</strong> that position - it never overwrites.</li>
                            <li><code>add</code> at <code>/-</code> <strong>appends</strong> to the end of the array.</li>
                            <li><code>remove</code> at an index deletes that element and <strong>shifts every later element one position left</strong>.</li>
                            <li>Operations apply <strong>in order</strong>, so each operation sees the array left behind by the previous one.</li>
                        </Box>

                        <DocSection>Append an item: the /- path</DocSection>
                        <DocCode label="Patch">{`[
  { "op": "add", "path": "/items/-", "value": "new item" }
]`}</DocCode>
                        <DocBeforeAfter
                            before={`{ "items": ["a", "b"] }`}
                            after={`{ "items": ["a", "b", "new item"] }`}
                        />
                        <DocText>
                            The <code>-</code> token comes from JSON Pointer (RFC 6901): it names the position one past
                            the last element of an array. Because it does not depend on the current length, it is the
                            safest way to append - you never need to count elements first.
                        </DocText>

                        <DocSection>Insert at a specific index</DocSection>
                        <DocCode label="Patch">{`[
  { "op": "add", "path": "/items/1", "value": "inserted" }
]`}</DocCode>
                        <DocBeforeAfter
                            before={`{ "items": ["a", "b", "c"] }`}
                            after={`{ "items": ["a", "inserted", "b", "c"] }`}
                        />
                        <DocText>
                            The new value lands <em>before</em> whatever was at that index, and everything from that
                            position on shifts one place right. The index may range from 0 up to the array length:
                            using the length itself appends, exactly like <code>/-</code>. An index greater than the
                            length is an error.
                        </DocText>

                        <DocSection>Empty arrays and missing parents</DocSection>
                        <DocText>
                            On an empty array, <code>/items/0</code> and <code>/items/-</code> both work and both mean
                            "first element". But the array itself must exist: <code>add /items/-</code> fails if the
                            document has no <code>items</code> key at all. Create the container first:
                        </DocText>
                        <DocCode label="Patch">{`[
  { "op": "add", "path": "/items", "value": [] },
  { "op": "add", "path": "/items/-", "value": "first" }
]`}</DocCode>

                        <DocSection>Replace an element (or the whole array)</DocSection>
                        <DocText>
                            <code>replace</code> changes the value at an exact path. Aimed at an index it swaps one
                            element; aimed at the array itself it swaps everything:
                        </DocText>
                        <DocCode label="Patch">{`[
  { "op": "replace", "path": "/items/1", "value": "B" }
]`}</DocCode>
                        <DocBeforeAfter
                            before={`{ "items": ["a", "b", "c"] }`}
                            after={`{ "items": ["a", "B", "c"] }`}
                        />

                        <DocSection>Remove an element - and the index shift</DocSection>
                        <DocCode label="Patch">{`[
  { "op": "remove", "path": "/items/1" }
]`}</DocCode>
                        <DocBeforeAfter
                            before={`{ "items": ["a", "b", "c"] }`}
                            after={`{ "items": ["a", "c"] }`}
                        />
                        <DocText>
                            After the removal, <code>c</code> moves from index 2 to index 1. This is the single most
                            common source of JSON Patch bugs: an index that was correct when you wrote the patch may
                            point somewhere else after an earlier operation runs.
                        </DocText>

                        <DocSection>Removing several elements in one patch</DocSection>
                        <DocText>
                            Because operations run in order and each removal shifts the tail left, the safe pattern is
                            to remove from the <strong>highest index to the lowest</strong>. To delete "b" and "d" from
                            ["a", "b", "c", "d"]:
                        </DocText>
                        <DocCode label="Patch - works">{`[
  { "op": "remove", "path": "/items/3" },
  { "op": "remove", "path": "/items/1" }
]`}</DocCode>
                        <DocCode label="Patch - removes the wrong elements">{`[
  { "op": "remove", "path": "/items/1" },
  { "op": "remove", "path": "/items/3" }
]`}</DocCode>
                        <DocText>
                            The second patch fails: after removing index 1 the array has only 3 elements, so index 3 no
                            longer exists. Removing the same index twice is the other classic trap - it deletes two
                            different elements, not the same one twice.
                        </DocText>

                        <DocSection>Why there are no wildcards</DocSection>
                        <DocText>
                            RFC 6902 has no wildcard or "all elements" syntax. A path segment is either an exact object
                            key or an exact array index - <code>/items/*</code> looks for a key literally
                            named <code>*</code> and fails on an array. To change every element, the patch must contain
                            one operation per element:
                        </DocText>
                        <DocCode label="Patch">{`[
  { "op": "replace", "path": "/items/0/done", "value": true },
  { "op": "replace", "path": "/items/1/done", "value": true },
  { "op": "replace", "path": "/items/2/done", "value": true }
]`}</DocCode>
                        <DocText>
                            Generating one operation per element is exactly what a diff does - paste your before and
                            after documents into the <Link href="/" style={{ color: 'var(--accent)' }}>generator</Link>{' '}
                            and it emits every operation for you. If you truly need "update all matching elements"
                            semantics, that is a loop in your application code producing a patch, not something a
                            single patch can express.
                        </DocText>

                        <DocSection>Arrays of objects and reordering</DocSection>
                        <DocText>
                            Paths keep walking through array elements into the objects inside them:
                            <code> /users/0/email</code> is the <code>email</code> of the first user. And{' '}
                            <code>move</code> reorders elements: moving <code>/users/0</code> to{' '}
                            <code>/users/2</code> takes the first user out and inserts them at position 2. Remember that
                            <code> move</code> removes first and inserts second, so the destination index applies to the
                            array <em>after</em> the removal.
                        </DocText>
                        <DocCode label="Patch">{`[
  { "op": "replace", "path": "/users/0/email", "value": "new@example.com" },
  { "op": "move", "from": "/users/0", "path": "/users/2" }
]`}</DocCode>

                        <DocSection>Guard array writes with test</DocSection>
                        <DocText>
                            Because indexes move, patches on arrays are fragile when the document can change between
                            generation and application. The{' '}
                            <Link href="/operations/test" style={{ color: 'var(--accent)' }}>test operation</Link>{' '}
                            stops evaluation if reality does not match your assumption:
                        </DocText>
                        <DocCode label="Patch">{`[
  { "op": "test", "path": "/items/1", "value": "b" },
  { "op": "remove", "path": "/items/1" }
]`}</DocCode>
                        <DocText>
                            If <code>/items/1</code> is no longer "b", nothing is removed. For APIs this is optimistic
                            concurrency control built into the format.
                        </DocText>

                        <DocSection>Common mistakes</DocSection>
                        <DocMistakeList items={mistakes} />

                        <DocSection>Reference</DocSection>
                        <DocText>
                            Array behavior is defined by{' '}
                            <a href="https://datatracker.ietf.org/doc/html/rfc6902#section-4.1" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--accent)' }}>RFC 6902, section 4.1 (add)</a>{' '}
                            and{' '}
                            <a href="https://datatracker.ietf.org/doc/html/rfc6902#section-4.2" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--accent)' }}>section 4.2 (remove)</a>.
                            The <code>-</code> token and index syntax come from{' '}
                            <a href="https://datatracker.ietf.org/doc/html/rfc6901#section-4" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--accent)' }}>RFC 6901, section 4</a>.
                        </DocText>

                        {/* CTA */}
                        <Box sx={{
                            mt: 6, p: 4, textAlign: 'center',
                            borderRadius: 'var(--radius-lg)',
                            background: 'var(--accent)', color: 'white',
                        }}>
                            <Typography variant="h2" sx={{ fontSize: '1.5rem', fontWeight: 600, mb: 2 }}>
                                Try it on a real array
                            </Typography>
                            <Typography sx={{ mb: 3, opacity: 0.9 }}>
                                Open the tool with an array example preloaded - generate the append patch yourself, or apply a removal.
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 1.5, justifyContent: 'center', flexWrap: 'wrap' }}>
                                <Button
                                    variant="contained"
                                    size="large"
                                    href={generateExampleUrl}
                                    endIcon={<ArrowForwardIcon />}
                                    sx={{
                                        backgroundColor: 'white', color: 'var(--accent)', fontWeight: 600,
                                        px: 4, py: 1.5, textTransform: 'none',
                                        '&:hover': { backgroundColor: 'rgba(255,255,255,0.9)' },
                                    }}
                                >
                                    Generate an array patch
                                </Button>
                                <Button
                                    size="large"
                                    href={applyExampleUrl}
                                    endIcon={<ArrowForwardIcon />}
                                    sx={{
                                        color: 'white', fontWeight: 600, px: 4, py: 1.5, textTransform: 'none',
                                        border: '1px solid rgba(255,255,255,0.6)',
                                        '&:hover': { borderColor: 'white', backgroundColor: 'rgba(255,255,255,0.08)' },
                                    }}
                                >
                                    Apply a removal
                                </Button>
                            </Box>
                        </Box>

                        <DocRelated links={[
                            { href: '/docs/json-pointer', title: 'JSON Pointer', description: 'The path syntax behind every array index, including /-' },
                            { href: '/operations/add', title: 'Add operation', description: 'Full reference for add on objects and arrays' },
                            { href: '/operations/remove', title: 'Remove operation', description: 'Full reference for remove and its error cases' },
                            { href: '/operations/test', title: 'Test operation', description: 'Guard array writes against concurrent changes' },
                            { href: '/docs', title: 'Documentation', description: 'Back to the JSON Patch documentation hub' },
                        ]} />
                    </Box>
                </Container>
            </main>
        </>
    );
                                }
